/**
 * Admin session state (ADMIN_FLOW.md §8.2). Separate from the tenant
 * authStore on purpose: the two sessions never share a token, a storage
 * key or a redirect target.
 */
import { create } from 'zustand'
import {
  adminAuthApi,
  getAdminToken,
  setAdminToken,
  setAdminUnauthorizedHandler,
  type AdminPrincipal,
} from './adminApi'

export type AdminAuthStatus = 'unknown' | 'authenticated' | 'anonymous'

export interface AdminAuthState {
  status: AdminAuthStatus
  admin: AdminPrincipal | null
  isLoggingIn: boolean
  error: string | null
  /** Validate any stored token against the server once, on panel mount. */
  bootstrap: () => Promise<void>
  login: (email: string, password: string) => Promise<boolean>
  logout: () => void
  reset: () => void
}

const initialState = {
  status: 'unknown' as AdminAuthStatus,
  admin: null,
  isLoggingIn: false,
  error: null,
}

export function loginErrorMessage(err: unknown): string {
  const status = (err as { response?: { status?: number } })?.response?.status
  if (status === 401) return 'Invalid email or password.'
  if (status === 429) return 'Too many attempts. Wait a minute and try again.'
  if (status === 422) return 'Enter your email and password.'
  return 'Could not reach the server. Try again.'
}

export const useAdminAuthStore = create<AdminAuthState>((set) => ({
  ...initialState,

  bootstrap: async () => {
    if (!getAdminToken()) {
      set({ status: 'anonymous', admin: null })
      return
    }
    try {
      const { data } = await adminAuthApi.me()
      set({ status: 'authenticated', admin: data })
    } catch {
      setAdminToken(null)
      set({ status: 'anonymous', admin: null })
    }
  },

  login: async (email, password) => {
    set({ isLoggingIn: true, error: null })
    try {
      const { data } = await adminAuthApi.login(email, password)
      setAdminToken(data.access_token)
      set({ status: 'authenticated', admin: data.admin, isLoggingIn: false, error: null })
      return true
    } catch (err) {
      setAdminToken(null)
      set({ status: 'anonymous', admin: null, isLoggingIn: false, error: loginErrorMessage(err) })
      return false
    }
  },

  logout: () => {
    setAdminToken(null)
    set({ status: 'anonymous', admin: null, error: null })
  },

  reset: () => {
    setAdminToken(null)
    set({ ...initialState })
  },
}))

// Any 401 from an admin call (expired token, account disabled) ends the session.
setAdminUnauthorizedHandler(() => {
  if (useAdminAuthStore.getState().status !== 'anonymous') useAdminAuthStore.getState().logout()
})
